import React, { useState } from 'react';
import { Box, Drawer as MuiDrawer, Stack, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loading from 'views/App/Portal/Loading';
import { error as errorColor, neutral } from '_lex/_foundations/phxColors';
import PanelToolbar from '_lex/_atoms/PanelToolbar';
import PanelFooter from '_lex/_atoms/PanelFooter';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface RightDrawerAction {
  key: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
}

export interface RightDrawerProps {
  header?: React.ReactNode;
  headerActions?: React.ReactNode;
  content: React.ReactNode;
  footer?: React.ReactNode;
  footerActions?: React.ReactNode;
  open: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  error?: string;
  setOpen?: (open: boolean) => void;
  noPadding?: boolean;
  hasToolbar?: boolean;
  wide?: boolean;
  verticalActions?: RightDrawerAction[];
}

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiPaper-root': {
    boxShadow: 'none',
  },
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    height: '100%',
    overflow: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: '64px',
    }),
  },
}));

const RightDrawer = ({
  header,
  headerActions,
  content,
  open,
  footer,
  footerActions,
  isLoading,
  hasError,
  error,
  setOpen,
  noPadding = false,
  hasToolbar = false,
  wide = false,
  verticalActions,
}: RightDrawerProps) => {
  const [actionContent, setActionContent] = useState<RightDrawerAction | null>(null);
  const showHeader = !!header || !!setOpen || !!headerActions;

  const handleToggleOpen = () => {
    if (!open && verticalActions && verticalActions.length > 0 && !actionContent) {
      const firstAction = verticalActions[0];
      firstAction.onClick();
    } else if (!open && verticalActions && verticalActions.length > 0 && actionContent) {
      actionContent?.onClick();
    }
    setOpen && setOpen(!open);
  };

  const toggleOpenButton = setOpen ? (
    <Button
      variant='text'
      color='neutral'
      size='small'
      className='icon-only xs'
      onClick={handleToggleOpen}
      sx={{ mr: 'auto' }}
    >
      {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
    </Button>
  ) : null;

  const drawerRows = () => {
    let gridRows = '1fr';
    if (showHeader) gridRows = `auto ${gridRows}`;
    if (footer) gridRows = `${gridRows} auto`;
    return gridRows;
  };

  const renderHeader = () => {
    if (!header) return undefined;
    if (typeof header === 'string') {
      return <Typography variant='subtitleLg'>{header}</Typography>;
    }
    return header;
  };

  return (
    <Drawer
      className='right-drawer'
      variant='permanent'
      open={open}
      anchor='right'
      sx={{
        backgroundColor: neutral[25],
        width: open ? (wide ? '500px' : '360px') : '64px',
        transition: 'width 0.5s ease',
        height: '100%',
        borderRadius: 0,
        overflow: 'hidden',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      {hasError ? (
        <Typography sx={{ color: errorColor[600] }}>{error}</Typography>
      ) : isLoading ? (
        <Box sx={{ mx: 'auto' }}>
          <Loading />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', height: '100%' }}>
          {verticalActions && (
            <Stack
              direction='column'
              spacing={2}
              sx={{
                alignItems: 'center',
                py: 4,
                px: 1,
                background: 'transparent',
                borderRight: '1px solid #e0e0e0',
                minWidth: 64,
                height: '100%',
              }}
            >
              {/* Arrow/collapse button at the top */}
              {toggleOpenButton}
              {/* Vertical action icons below */}
              {verticalActions &&
                verticalActions.map((action) => (
                  <Tooltip
                    key={action.key}
                    title={action.label}
                    placement='left'
                  >
                    <span>
                      <IconButton
                        onClick={() => {
                          action.onClick();
                          setActionContent(action);
                        }}
                        color={action.active ? 'primary' : 'default'}
                        size='large'
                        sx={{
                          background: action.active ? 'rgba(0,0,0,0.04)' : 'transparent',
                          mb: 1,
                        }}
                      >
                        {action.icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                ))}
            </Stack>
          )}
          <Box sx={{ flex: 1, display: 'grid', gridTemplateRows: drawerRows(), height: '100%' }}>
            {showHeader && (
              <PanelToolbar
                leftItems={open ? renderHeader() : undefined}
                rightItems={<>{open && headerActions}</>}
              />
            )}
            <Box
              sx={{
                overflowY: 'auto',
                p: noPadding ? 0 : 4,
              }}
            >
              {open && content}
            </Box>
            {footer && open && (
              <PanelFooter
                leftItems={footer}
                rightItems={footerActions}
              />
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default RightDrawer;
